/**
 * Work a click starts (a download, a request) and the buttons that wait for it. `busy(key)` is true from the click
 * until the work settles, so the button shows the kit's spinner (`Button loading`) at once instead of nothing
 * until the browser's download starts. Keys are per button: the others stay usable. A second click on a busy key
 * does nothing (no second download) and resolves with `undefined`. The work's own promise is returned otherwise,
 * so the caller still handles the result and the error (e.g. a toast). Nothing is blocked or overlaid.
 */
import { useCallback, useRef, useState } from "react";

/**
 * For work whose caller has nothing to show on failure (a retry): the error is thrown again outside the promise,
 * so it still reaches the global error reporting instead of being swallowed.
 */
export function rethrowLater(error: unknown): void {
  queueMicrotask(() => {
    throw error;
  });
}

export interface Pending<K extends string> {
  /** True while the work started under `key` runs; without a key, while any does. */
  busy: (key?: K) => boolean;
  /** Runs `work` under `key` unless that key is already busy (then resolves with `undefined`, nothing runs). */
  run: <T>(key: K, work: () => Promise<T>) => Promise<T | undefined>;
}

export function usePending<K extends string = string>(): Pending<K> {
  // The ref answers a second click in the same frame; the state re-renders the spinner.
  const running = useRef(new Set<K>());
  const [keys, setKeys] = useState<ReadonlySet<K>>(() => new Set());
  const run = useCallback(<T>(key: K, work: () => Promise<T>): Promise<T | undefined> => {
    if (running.current.has(key)) return Promise.resolve(undefined);
    running.current.add(key);
    setKeys(new Set(running.current));
    // Started now (inside the click), and a throw before the promise exists still ends the spinner.
    const p = (async () => work())();
    const done = () => {
      running.current.delete(key);
      setKeys(new Set(running.current));
    };
    return p.finally(done);
  }, []);
  const busy = useCallback((key?: K) => (key === undefined ? keys.size > 0 : keys.has(key)), [keys]);
  return { busy, run };
}
